import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { MEMBER_HOME_PATH, sanitizeAuthReturnPath, signedInLanding } from '@/lib/privacy/auth-document';
import { useModalStore } from '@/lib/stores/modal-store';
import { logger } from '@/lib/utils/logger';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2,X } from 'lucide-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AUTH_CARD_CLASS, AuthScreen } from './AuthScreen';
import { AuthSuccess } from './AuthSuccess';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'sign-in' | 'sign-up';
  redirectTo?: string;
}

export function AuthModal({ 
  isOpen, 
  onClose, 
  defaultTab = 'sign-in', 
  redirectTo 
}: AuthModalProps) {
  const { error, isLoading, user: _user, profile: _profile } = useUnifiedAuth();
  const { authModal, showAuthSuccess } = useModalStore();
  const [activeTab, setActiveTab] = React.useState<'sign-in' | 'sign-up'>(defaultTab);
  const [showForgotPassword, _setShowForgotPassword] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const successTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [pendingSuccess, setPendingSuccess] = React.useState(false);
  const [_showEmailLogin, _setShowEmailLogin] = React.useState(false);
  const [_promptPhoneLink, _setPromptPhoneLink] = React.useState(false);

  const showSuccess = authModal.showSuccess;
  const successMessage = authModal.successMessage || '';

  React.useEffect(() => {
    if (isOpen) {
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      window.sessionStorage.setItem(
        'authRedirectPath',
        sanitizeAuthReturnPath(redirectTo || currentPath),
      );
    }
  }, [isOpen, location, redirectTo]);

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    } else {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    }
  }, [isOpen, defaultTab, isLoading, showSuccess]);

  React.useEffect(() => {
    if (isLoading) {
      setIsTransitioning(true);
      setPendingSuccess(false);
    } else if (isTransitioning && !isLoading && !showSuccess && !error) {
      setPendingSuccess(true);
      // Keep transitioning state active until we get success or timeout
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setPendingSuccess(false);
      }, 500); // Give more time for success state to be set
      return () => clearTimeout(timer);
    } else if (showSuccess) {
      setIsTransitioning(false);
      setPendingSuccess(false);
    } else if (error) {
      setIsTransitioning(false);
      setPendingSuccess(false);
    }
  }, [isLoading, showSuccess, isTransitioning, error]);

  const handleSuccess = React.useCallback((message: string) => {
    logger.info('AuthModal handleSuccess called', { message, hasError: !!error });
    
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    
    setPendingSuccess(false);
    
    showAuthSuccess(message);
  }, [showAuthSuccess, error]);

  React.useEffect(() => {
    if (showSuccess) {
      successTimeoutRef.current = setTimeout(() => {
        onClose();
      }, 3000);

      return () => {
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = null;
        }
      };
    }
  }, [showSuccess, onClose]);

  const getHeaderText = () => {
    if (showSuccess) return 'Success!';
    if (showForgotPassword) return 'Reset Password';
    return activeTab === 'sign-up' ? 'Join Rendezvous' : 'Welcome Back';
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={showSuccess ? undefined : onClose}>
      <Dialog.Portal>
        <Dialog.Overlay 
          className="fixed inset-0 bg-earth-900/80 backdrop-blur-sm z-40"
        />
        
        <Dialog.Content className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${AUTH_CARD_CLASS} max-h-[90vh] flex flex-col z-50`}>
          <div className="flex-1 overflow-y-auto">
            {showSuccess ? (
              <>
                <div className="flex items-center justify-between p-6 border-b border-earth-700">
                  <Dialog.Title className="text-2xl font-brand-display text-sand-100">
                    {getHeaderText()}
                  </Dialog.Title>
                  <Dialog.Close className="text-sand-400 hover:text-sand-300" disabled>
                    <X className="h-5 w-5" />
                  </Dialog.Close>
                </div>
                <AuthSuccess
                  message={successMessage}
                  submessage={activeTab === 'sign-up'
                    ? 'Your account has been created successfully.'
                    : "You're now logged in to your account."}
                />
              </>
            ) : isLoading || isTransitioning || pendingSuccess ? (
              <>
                <div className="flex items-center justify-between p-6 border-b border-earth-700">
                  <Dialog.Title className="text-2xl font-brand-display text-sand-100">
                    {getHeaderText()}
                  </Dialog.Title>
                  <Dialog.Close className="text-sand-400 hover:text-sand-300">
                    <X className="h-5 w-5" />
                  </Dialog.Close>
                </div>
                <div className="p-6 flex flex-col items-center justify-center">
                  <Loader2 className="h-8 w-8 text-sand-400 animate-spin mb-4" />
                  <p className="text-sand-300">
                    {pendingSuccess ? 'Success! Preparing your dashboard...' : 'Authenticating...'}
                  </p>
                </div>
              </>
            ) : (
              <AuthScreen
                activeTab={activeTab}
                onTabChange={setActiveTab}
                Title={Dialog.Title}
                close={(
                  <Dialog.Close className="text-sand-400 hover:text-sand-300">
                    <X className="h-5 w-5" />
                  </Dialog.Close>
                )}
                onVerified={() => {
                  handleSuccess(
                    activeTab === 'sign-up'
                      ? 'Thanks — you\'re on the list. Pending approval.'
                      : 'Signed in',
                  );
                  // REN-513: an in-page sign-in that started on home lands on the
                  // members page when it is switched on, like the /login document.
                  const startedAt = sanitizeAuthReturnPath(
                    redirectTo || `${location.pathname}${location.search}${location.hash}`,
                  );
                  if (
                    signedInLanding(startedAt) === MEMBER_HOME_PATH &&
                    location.pathname !== MEMBER_HOME_PATH
                  ) {
                    navigate(MEMBER_HOME_PATH, { replace: true });
                  }
                }}
              />
            )}
          </div>

          {error && !isLoading && !showSuccess && (
            <div className="p-4 border-t border-earth-700 bg-red-500/10">
              <p className="text-sm text-red-500 text-center">
                {error.message || 'An error occurred'}
              </p>
            </div>
          )}
          {/* MemberRequestModal intentionally not mounted here */}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
